
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { API_BASE_URL } from '@/config/api';
import { useToast } from '@/hooks/use-toast';

interface SendEmailModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedAnalysis?: {
    id: number;
    attributes: {
      patientEmail?: string;
      patient_name?: string;
    };
  };
}

export const SendEmailModal = ({ isOpen, onClose, selectedAnalysis }: SendEmailModalProps) => {
  const [patientEmail, setPatientEmail] = useState("");
  const { toast } = useToast();
  const { t } = useTranslation();

  // Update state when selected analysis changes or modal opens
  useEffect(() => {
    if (selectedAnalysis && isOpen) {
      setPatientEmail(selectedAnalysis.attributes.patientEmail || "");
    }
  }, [selectedAnalysis, isOpen]);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPatientEmail(e.target.value);
  };

  const confirmSendEmail = async () => {
    if (!selectedAnalysis) return;
    
    try {
      const jwt = localStorage.getItem('token');
      
      // First update the patient email if it's different
      if (patientEmail !== selectedAnalysis.attributes.patientEmail) {
        await axios.put(`${API_BASE_URL}/analyses/${selectedAnalysis.id}`, {
          data: {
            patientEmail: patientEmail,
          },
        }, {
          headers: {
            Authorization: `Bearer ${jwt}`,
            'Content-Type': 'application/json',
          },
        });
      }

      // Then send the report
      await axios.post(`${API_BASE_URL}/send-report`, {
        analysisId: selectedAnalysis.id,
      }, {
        headers: {
          Authorization: `Bearer ${jwt}`,
        },
      });

      toast({
        title: t('email_sent_title'),
        description: t('email_sent_description'),
      });

      onClose();
    } catch (error) {
      console.error('Error al enviar el email:', error);
      toast({
        title: t('email_error_title'),
        description: t('email_error_description'),
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('send_email_modal_title')}</DialogTitle>
        </DialogHeader>
        <div className="mt-4">
          <Input 
            value={patientEmail} 
            onChange={handleEmailChange} 
            type="email" 
            placeholder={t('send_email_modal_placeholder')}
          />
        </div>
        <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0">
          <Button 
            variant="secondary" 
            onClick={onClose} 
            className="bg-gray-200 hover:bg-gray-300 text-black w-full sm:w-auto sm:mr-2"
          >
            {t('cancel')}
          </Button>
          <Button 
            onClick={confirmSendEmail} 
            className="bg-[#257272] hover:bg-[#257272] text-white w-full sm:w-auto"
          >
            {t('send_report')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
